import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { CheckStateType } from '@Components/designConstants';
import { useRequiredContext } from '@src/utils/hooks/useRequiredContext';
import { ActivityId } from './activity-feed-data-utils';
import { getCheckState, pruneCheckedIds, toggleCheckedId } from './List/activity-feed-list-utils';

export interface MultiSelectValue {
  checkedIds: ReadonlySet<ActivityId>;
  checkState: CheckStateType;
  totalCount: number;
  toggleCheckedId: (id: ActivityId) => void;
  setAllChecked: (checked: boolean) => void;
}

const MultiSelectContext = createContext<MultiSelectValue | null>(null);

export const useMultiSelect = (): MultiSelectValue =>
  useRequiredContext(MultiSelectContext, 'useMultiSelect', 'MultiSelectProvider');

interface MultiSelectProviderProps {
  activityIds: ActivityId[];
  children: ReactNode;
}

export const MultiSelectProvider = ({
  activityIds,
  children
}: MultiSelectProviderProps): ReactNode => {
  const [storedIds, setStoredIds] = useState<ReadonlySet<ActivityId>>(() => new Set());

  // Unlike the detail selection, checks are dropped once a filter or search hides an event,
  // so bulk actions only ever touch what the user can see.
  useEffect(() => {
    setStoredIds(prev => pruneCheckedIds(prev, activityIds));
  }, [activityIds]);
  // Covers the render between an activityIds change and the prune effect.
  const checkedIds = useMemo(
    () => pruneCheckedIds(storedIds, activityIds),
    [storedIds, activityIds]
  );

  const toggle = useCallback(
    (id: ActivityId) => setStoredIds(prev => toggleCheckedId(prev, id)),
    []
  );
  const setAllChecked = useCallback(
    (checked: boolean) => setStoredIds(checked ? new Set(activityIds) : new Set()),
    [activityIds]
  );

  const value = useMemo<MultiSelectValue>(
    () => ({
      checkedIds,
      checkState: getCheckState(checkedIds.size, activityIds.length),
      totalCount: activityIds.length,
      toggleCheckedId: toggle,
      setAllChecked
    }),
    [checkedIds, activityIds.length, toggle, setAllChecked]
  );

  return <MultiSelectContext.Provider value={value}>{children}</MultiSelectContext.Provider>;
};
